import {Plural, Trans} from '@lingui/macro';
import React, {FC, Fragment, ReactNode, useMemo} from 'react';
import styled from 'styled-components';

import {ClickEventHandler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {SheetEmoji} from '../../components/common/emoji/sheetEmoji';
import {
  ExpandableResourceCell,
  ExpandableResourceCellIconProps,
  ExpandableResourceCellInheritableProps,
} from '../../components/common/table/cells/expandableResourceCell';
import {TableCellProps} from '../../components/common/table/cells/tableCell';
import {getTagEmojiHighlight} from '../../helpers/models/highlightHelpers';
import {getTagChildrenIds} from '../../helpers/models/tagHelpers';
import {HighlightTypesEnum} from '../../models/api/highlightBaseModel';
import {TagModel} from '../../models/api/tagModel';

/*
 * Props.
 */

interface SettingsTagNameCellProps extends TableCellProps<TagModel>, SettingsTagNameCellInheritableProps {
  value: TagModel;
  parentTags: ReadonlyArray<TagModel>;
  nestingLevel: number;
  onClick: ClickEventHandler;
}

export interface SettingsTagNameCellInheritableProps
  extends ExpandableResourceCellInheritableProps<TagModel> {
  isInSearch: boolean;
  isInArchivedView: boolean;
}

/*
 * Style.
 */

export const StyledBoxedEmoji = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  width: 40px;
  height: 40px;
  box-sizing: border-box;
  background-color: ${(p) => p.theme.greys.white};
  border: 1px ${(p) => p.theme.alphas.gray10} solid;
`;

const StyledTabSubtitleIcon = styled(Icon)`
  display: inline-block;
  transform: translateY(2px);
`;

/*
 * Component.
 */

export const SettingsTagNameCell: FC<SettingsTagNameCellProps> = (props) => {
  const theme = useUserTheme();
  const {
    isSelected,
    isHovering,
    isLoading,
    value,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    isInArchivedView,
  } = props;

  const hasChildren = getTagChildrenIds(props.value, isInArchivedView).length > 0;
  const nestingLevel = props.isInSearch ? 0 : Math.min(props.nestingLevel || 0, 3);

  const shouldShowSubtitle = hasChildren || props.isInSearch;
  const shouldHideActions = () => {
    if (!isInArchivedView) {
      return false;
    }

    return nestingLevel > 0;
  };

  const iconProps: ExpandableResourceCellIconProps = useMemo(() => {
    if (value.type === HighlightTypesEnum.EMOJI) {
      const definition = getTagEmojiHighlight(value);
      if (definition) {
        return {
          node: (
            <StyledBoxedEmoji>
              <SheetEmoji emoji={definition.emoji} skinTone={definition.skinTone} size={20} />
            </StyledBoxedEmoji>
          ),
        };
      }
    }

    const tagColor =
      value.type === HighlightTypesEnum.COLOR
        ? theme.palette[value.highlightColor].shade40
        : theme.greys.shade70;

    return {
      color: tagColor,
      name: 'tagFilledSmall',
      borderColor: theme.alphas.gray10,
    };
  }, [value]);

  return (
    <ExpandableResourceCell<TagModel>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      nestingLevel={nestingLevel}
      hasChildren={hasChildren}
      shouldShowSubtitle={shouldShowSubtitle}
      shouldHideActions={shouldHideActions()}
      value={props.value}
      onClick={props.onClick}
      onChevronClick={props.onChevronClick}
      onActionsDropdownOpened={props.onActionsDropdownOpened}
      renderActionsDropdown={({modalProps, value: tag}) =>
        props.renderActionsDropdown({modalProps, value: tag as TagModel})
      }
      hasChevron={props.hasChevron}
      hasHover={props.hasHover}
      isExpanded={props.isExpanded}
      renderSubtitle={({seperatorNode}) => maybeRenderSubtitle(props, seperatorNode)}
      iconProps={iconProps}
    />
  );
};

function maybeRenderSubtitle(props: SettingsTagNameCellProps, sep: ReactNode) {
  const hasChildren = getTagChildrenIds(props.value, props.isInArchivedView).length > 0;

  if (!hasChildren && !props.isInSearch) {
    return null;
  }

  const maybeRenderParentTags = () => {
    if (!props.isInSearch || props.parentTags.length === 0) {
      return null;
    }

    return (
      <>
        <Trans id="settingstable-inparents">
          In <TagParentsFormatter parentTags={props.parentTags} />
        </Trans>
        {hasChildren && sep}
      </>
    );
  };

  return (
    <>
      {maybeRenderParentTags()}
      {hasChildren && (
        <Plural
          id="settingstable-subtags"
          value={getTagChildrenIds(props.value, props.isInArchivedView).length}
          one="# subtag"
          other="# subtags"
        />
      )}
    </>
  );
}

interface TagParentsFormatterProps {
  parentTags: ReadonlyArray<TagModel>;
}
const TagParentsFormatter: FC<TagParentsFormatterProps> = (props) => (
  <>
    {props.parentTags.map((t, index, array) => {
      const isLast = index === array.length - 1;

      return (
        <Fragment key={t.id}>
          {t.name}
          {!isLast && <StyledTabSubtitleIcon name="chevronRightTiny" />}
        </Fragment>
      );
    })}
  </>
);
